import { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Mail, Key, Copy, Check, AlertTriangle, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../../utils/apiUrl';

const ForgotPasswordModal = ({ isOpen, onClose, onProceedToReset }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetData, setResetData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/auth/forgot-password`, { email });
      setResetData(response.data);
      
      if (response.data.success && response.data.reset_token) {
        toast.success('Reset code generated successfully');
      } else if (response.data.success) {
        toast.info('If an account exists, reset instructions have been sent');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to process request';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToken = () => {
    if (resetData?.reset_token) {
      navigator.clipboard.writeText(resetData.reset_token);
      setCopied(true);
      toast.success('Reset code copied to clipboard');
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const handleProceedToReset = () => {
    onClose();
    onProceedToReset(resetData?.reset_token);
  };

  const handleClose = () => {
    setEmail('');
    setResetData(null);
    setError('');
    setCopied(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-slate-900 border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Key className="w-5 h-5 text-[#C5A059]" />
            Forgot Password
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Enter your registered email to receive a password reset code.
          </DialogDescription>
        </DialogHeader>

        {!resetData ? (
          // Step 1: Enter email
          <form onSubmit={handleRequestReset} className="space-y-4 mt-4">
            {error && (
              <Alert className="bg-red-500/10 border-red-500/30">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <AlertDescription className="text-red-400">{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label className="text-slate-300">Registered Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-vault pl-10"
                  placeholder="you@example.com"
                  required
                  data-testid="forgot-password-email"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1 border-white/10"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading || !email}
                className="flex-1 btn-gold"
                data-testid="forgot-password-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Get Reset Code'
                )}
              </Button>
            </div>
          </form>
        ) : (
          // Step 2: Show reset token
          <div className="space-y-4 mt-4">
            {resetData.show_token_on_screen && resetData.reset_token ? (
              <>
                <Alert className="bg-amber-500/10 border-amber-500/30">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  <AlertDescription className="text-amber-300 text-sm">
                    <strong>Important:</strong> Email/SMS services are not yet integrated. 
                    Please copy and save this reset code before proceeding.
                  </AlertDescription>
                </Alert>

                <div className="p-4 rounded-lg bg-slate-800/50 border border-white/10">
                  <Label className="text-slate-400 text-xs mb-2 block">Your Reset Code</Label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-[#C5A059] font-mono text-sm bg-slate-900 px-3 py-2 rounded border border-[#C5A059]/30 break-all">
                      {resetData.reset_token}
                    </code>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={handleCopyToken}
                      className="border-[#C5A059]/30 hover:bg-[#C5A059]/10"
                      data-testid="copy-reset-token"
                    >
                      {copied ? (
                        <Check className="w-4 h-4 text-emerald-400" />
                      ) : (
                        <Copy className="w-4 h-4 text-[#C5A059]" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    Expires in {resetData.expires_in_minutes} minutes
                  </p>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    className="flex-1 border-white/10"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={handleProceedToReset}
                    className="flex-1 btn-gold"
                    data-testid="proceed-to-reset"
                  >
                    Proceed to Reset Password
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </>
            ) : (
              // When notifications are working
              <div className="text-center py-4">
                <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-emerald-400" />
                </div>
                <p className="text-white font-medium mb-2">{resetData.message}</p>
                <p className="text-slate-400 text-sm">
                  Check your email for the password reset link.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  className="mt-4 border-white/10"
                >
                  Close
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ForgotPasswordModal;
